import React, { useState, useEffect, useRef } from 'react';
import { User, onAuthStateChanged, signOut } from 'firebase/auth';
import { Navigate, useLocation } from 'react-router-dom';
import { getFirebaseAuth } from '../firebase';
import { ChangePassword } from './ChangePassword';

// Routes served without authentication. Everything else requires a user
// and bounces to /login when there isn't one. /explore/venezuela is the
// no-login free tier — see FreeMapPage.tsx.
const PUBLIC_PATHS = new Set(['/', '/login', '/explore/venezuela']);
const BriefingModal = React.lazy(() => import('./BriefingModal').then(m => ({ default: m.BriefingModal })));
const PreferencesModal = React.lazy(() => import('./PreferencesModal'));
import { useBriefing } from '../hooks/useBriefing';
import { useUserProfile } from '../hooks/useUserProfile';
import { onForegroundMessage } from '../pushNotifications';
import { identifyUser } from '../analytics';

// ── Inactivity auto-logout ───────────────────────────────────────────────
// Tracks "last activity" as a timestamp in localStorage (shared across
// tabs in the same browser). A short polling interval + visibilitychange
// listener decide when to sign out, rather than relying on a single long
// setTimeout (which background tabs throttle / freeze).
//
// Why this shape:
//   - Multi-tab: any active tab updates the timestamp; idle tabs see it
//     on their next poll and don't fire a premature signOut(). Fixes the
//     "active user gets booted because another tab was idle" symptom.
//   - Background tabs: polling every 30s is not subject to the heavy
//     throttling browsers apply to long setTimeouts. Fixes "users who
//     switch tabs never get logged out".
//   - Persistence: closing and reopening a tab keeps the timestamp, so
//     a user who's been away >30 min is signed out on reopen.
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 min — must match server session expectations
const ACTIVITY_KEY = 'mosaics:lastActivity';
const ACTIVITY_THROTTLE_MS = 5 * 1000;        // write at most every 5s — localStorage is cheap but not free
const POLL_INTERVAL_MS = 30 * 1000;           // check every 30s — worst-case "late" signout is 30s

const readLastActivity = (): number => {
  try {
    const raw = localStorage.getItem(ACTIVITY_KEY);
    return raw ? parseInt(raw, 10) || 0 : 0;
  } catch { return 0; }
};

const writeLastActivity = (ts: number): void => {
  try { localStorage.setItem(ACTIVITY_KEY, String(ts)); } catch { /* storage disabled */ }
};

const clearLastActivity = (): void => {
  try { localStorage.removeItem(ACTIVITY_KEY); } catch { /* storage disabled */ }
};

interface AuthGateProps {
  children: React.ReactNode;
}

export const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const { profile, mergedModules, fetchProfile } = useUserProfile();
  const location = useLocation();

  // Preferences modal state. Two trigger paths:
  //   • "first-run"  — profile loaded but `preferences.completed_at` is
  //                     null; auto-open BEFORE the briefing fires.
  //   • user menu    — the user clicks Preferences in UserProfile; it
  //                     fires `mosaics:open-preferences` which we listen
  //                     for below.
  const [prefsOpen, setPrefsOpen] = useState(false);
  const prefsNeedsFirstRun = !!profile && !profile.preferences?.completed_at;

  useEffect(() => {
    if (prefsNeedsFirstRun && !needsPasswordChange) setPrefsOpen(true);
  }, [prefsNeedsFirstRun, needsPasswordChange]);

  useEffect(() => {
    const handler = () => setPrefsOpen(true);
    window.addEventListener('mosaics:open-preferences', handler);
    return () => window.removeEventListener('mosaics:open-preferences', handler);
  }, []);

  // Trigger a profile refetch whenever a user signs IN (uid becomes
  // non-null). useUserProfile mounts once with `[]` deps and reads
  // auth.currentUser directly; if Firebase hadn't finished propagating
  // the sign-in yet, that first fetch bails with "Not authenticated"
  // and never retries — leaving `profile` null, which (a) hides the
  // first-run Preferences modal and (b) breaks the module auto-swap
  // below. Re-firing fetchProfile the moment we observe a new uid
  // closes that race.
  const prevUidRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevUidRef.current;
    const curr = user?.uid ?? null;
    if (curr && curr !== prev) {
      void fetchProfile();
    }
    prevUidRef.current = curr;
  }, [user?.uid, fetchProfile]);

  // Default-module landing is handled by LoginPage: it holds on a
  // "Loading your workspace…" screen while /api/user/profile resolves,
  // then navigates once to the correct module. No AuthGate swap is
  // needed, so a user who later browses to /map from the sidebar isn't
  // bounced back out.

  const briefing = useBriefing();

  // The briefing only fires on the map view (route "/map"). Visiting
  // admin, research, alerts, api-keys, or the marketing splash at "/"
  // skips the status check, the fetch, and the modal render entirely.
  const onMapView = location.pathname === '/map';
  const isPublicPath = PUBLIC_PATHS.has(location.pathname);

  // Inactivity auto-logout. Depends only on whether a user is signed in
  // (not the User object reference) so token refreshes don't re-run the
  // effect and accidentally reset the activity timestamp.
  const userActive = !!user;
  useEffect(() => {
    if (!userActive) return;

    let lastWriteAt = 0;
    let signingOut = false;

    // On (re)mount with an active user:
    //   - no prior value → fresh login. Start a window now.
    //   - stale value (>timeout old) → leave it alone; the immediate
    //     checkTimeout() at the bottom signs them out.
    //   - recent value → bump to now so the user gets a full window.
    const initialLast = readLastActivity();
    if (!initialLast || Date.now() - initialLast <= INACTIVITY_TIMEOUT_MS) {
      writeLastActivity(Date.now());
      lastWriteAt = Date.now();
    }

    const recordActivity = (): void => {
      const now = Date.now();
      if (now - lastWriteAt < ACTIVITY_THROTTLE_MS) return;
      lastWriteAt = now;
      writeLastActivity(now);
    };

    const checkTimeout = async (): Promise<void> => {
      if (signingOut) return;
      const last = readLastActivity();
      if (last && Date.now() - last > INACTIVITY_TIMEOUT_MS) {
        signingOut = true;
        try {
          const auth = await getFirebaseAuth();
          if (auth.currentUser) {
            console.log('Signing out due to inactivity');
            clearLastActivity();
            await signOut(auth);
          }
        } catch (err) {
          console.error('Inactivity sign-out error:', err);
          signingOut = false;
        }
      }
    };

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'] as const;
    activityEvents.forEach((ev) => document.addEventListener(ev, recordActivity, { passive: true }));

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') { void checkTimeout(); }
    };
    document.addEventListener('visibilitychange', onVisible);

    const pollId = setInterval(() => { void checkTimeout(); }, POLL_INTERVAL_MS);

    // Catches "user reopened a stale tab" immediately on mount.
    void checkTimeout();

    return () => {
      activityEvents.forEach((ev) => document.removeEventListener(ev, recordActivity));
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(pollId);
      // Clear the shared timestamp so a subsequent login starts a fresh
      // window. Without this, sign-out followed by re-login could read a
      // stale value and trip the immediate-signout path again.
      clearLastActivity();
    };
  }, [userActive]);

  useEffect(() => {
    let unsubscribe: () => void;

    const initAuth = async () => {
      try {
        const auth = await getFirebaseAuth();
        
        unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
          // Set user + loading FIRST, before any async work. Awaiting the
          // profile fetch here used to leave `user=null` for hundreds of
          // ms after Firebase signaled a successful sign-in. LoginPage
          // would already have navigated to /map by then, AuthGate would
          // still see user=null, render <Navigate to="/login">, LoginPage
          // would see the signed-in user again, navigate to /map — you
          // get the picture. Chrome throttled the resulting flood and
          // the app stuck on "Loading…".
          setUser(currentUser);
          setLoading(false);
          if (!currentUser) {
            setNeedsPasswordChange(false);
            return;
          }
          // Profile fetch happens AFTER user is committed to state. Worst
          // case for users with forcePasswordChange=true: brief flash of
          // the app before we swap them onto <ChangePassword>. That's
          // rare and preferable to a login redirect loop.
          try {
            const token = await currentUser.getIdToken();
            const response = await fetch('/api/user/profile', {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const profile = await response.json();
              setNeedsPasswordChange(profile.forcePasswordChange === true);
            } else {
              console.error('Failed to fetch profile:', response.status);
              setNeedsPasswordChange(false);
            }
          } catch (err) {
            console.error('Error checking profile:', err);
            setNeedsPasswordChange(false);
          }
        });
      } catch (err: any) {
        console.error('Auth initialization failed:', err);
        setError('Failed to connect to security services.');
        setLoading(false);
      }
    };

    initAuth();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Push the Firebase UID (or null on sign-out) into GA4. identifyUser is
  // a no-op when analytics isn't configured, so this fires safely in dev
  // too. Depends only on uid so a token refresh doesn't re-fire it.
  const uid = user?.uid ?? null;
  useEffect(() => { identifyUser(uid); }, [uid]);

  // Briefing is gated on BOTH the password-change flow AND the first-run
  // Preferences modal: the first thing a brand-new account sees is the
  // Preferences modal, then — if they left the "Show the daily briefing"
  // toggle on — the briefing. An existing user with `completed_at`
  // already set skips the first gate entirely.
  const briefingEnabled = profile?.preferences?.briefing?.enabled !== false;
  // `!profile` keeps the briefing from racing in BEFORE we know whether
  // we need to show the first-run Preferences modal. Without it, a slow
  // profile fetch lets the briefing render first and the user never
  // sees the Preferences prompt on fresh accounts.
  const briefingBlocked = !profile || prefsNeedsFirstRun || prefsOpen || !briefingEnabled;

  useEffect(() => {
    if (onMapView && user && !needsPasswordChange && !briefingBlocked) {
      briefing.checkBriefingStatus();
    }
  }, [onMapView, user, needsPasswordChange, briefingBlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch briefing data when we know we should show it. Map-only.
  // Pass the user's saved briefing preferences through so the hook can
  // (a) use the pinned country list instead of auto-ranking, and
  // (b) skip the LLM-summary / premise-results fetches if the user
  // disabled those sections.
  useEffect(() => {
    if (onMapView && briefing.shouldShow && !briefingBlocked && !briefing.countries && !briefing.loading && !briefing.error) {
      briefing.fetchBriefing({
        countries: profile?.preferences?.briefing?.countries,
        sections:  profile?.preferences?.briefing?.sections,
      });
    }
  }, [onMapView, briefing.shouldShow, briefingBlocked, briefing.countries, briefing.loading, briefing.error, profile]); // eslint-disable-line react-hooks/exhaustive-deps

  // FCM foreground toast — listens while any page is open
  const [toast, setToast] = useState<{ title?: string; body?: string } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user) return;
    const unsub = onForegroundMessage((payload) => {
      setToast({ title: payload.title, body: payload.body });
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(null), 6000);

      // OS-level notification — must come from the service worker rather
      // than `new Notification(...)`. Modern Chrome (especially on Android)
      // throws an "Illegal constructor" TypeError when the page-level
      // constructor is invoked from a non-user-gesture callback, so the
      // SW path is the only reliable cross-platform option.
      if (
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted' &&
        'serviceWorker' in navigator
      ) {
        navigator.serviceWorker.ready
          .then((reg) =>
            reg.showNotification(payload.title || 'Mosaics', {
              body: payload.body || '',
              data: { ...(payload.data || {}), url: payload.data?.deep_link },
              icon: '/icon-192.png',
              tag: payload.data?.canonical_id || payload.data?.summary_id || undefined,
            }),
          )
          .catch((e) => console.warn('[push] showNotification failed:', e));
      }
    });
    return () => {
      unsub();
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, [user]);

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div>Loading Mosaics Security...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: 'red' }}>
        <h3>System Error</h3>
        <p>{error}</p>
      </div>
    );
  }

  // Public paths (marketing splash, login) render without authentication.
  // We reach this branch AFTER the loading state resolves so onAuthStateChanged
  // has had a chance to populate `user` — LoginPage can then react to that
  // state and navigate to /map on a successful sign-in.
  if (isPublicPath) {
    return <>{children}</>;
  }

  // Protected route + no user → bounce to the login page. `replace` so the
  // browser back button doesn't dump the user right back on the protected
  // route without credentials.
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (needsPasswordChange) {
    return (
      <ChangePassword 
        user={user} 
        onSuccess={() => setNeedsPasswordChange(false)} 
      />
    );
  }

  return (
    <>
      {/* Preferences modal — opened on first login (before briefing) OR
          from the user-icon menu via the `mosaics:open-preferences`
          event we listen for above. Mounted exactly once per authed
          session so a double-click can't double-mount it. */}
      {prefsOpen && (
        <React.Suspense fallback={null}>
          <PreferencesModal
            open={prefsOpen}
            onClose={() => setPrefsOpen(false)}
          />
        </React.Suspense>
      )}

      {onMapView && briefing.shouldShow && !briefingBlocked && (
        <React.Suspense fallback={null}>
        <BriefingModal
          countries={briefing.countries}
          forms={briefing.forms}
          changes={briefing.changes}
          aiSummary={briefing.aiSummary}
          loading={briefing.loading}
          error={briefing.error}
          onNavigate={(path) => {
            briefing.dismissBriefing();
            if (window.location.pathname !== path) {
              window.location.href = path;
            }
          }}
          hideResearch={!mergedModules.research}
          /* Section-visibility prefs — default true when the user hasn't
             touched them, matches the server-side fetch logic. The modal
             hides each section (and the "While you were away" header
             when only the AI summary is in play) accordingly. */
          sectionsEnabled={{
            summary: profile?.preferences?.briefing?.sections?.summary !== false,
            premise: profile?.preferences?.briefing?.sections?.premise !== false,
            events:  profile?.preferences?.briefing?.sections?.events  !== false,
          }}
        />
        </React.Suspense>
      )}
      {children}

      {/* FCM foreground toast */}
      {toast && (
        <div
          onClick={() => setToast(null)}
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 99999,
            background: 'linear-gradient(135deg, #00203F 0%, #004777 100%)',
            border: '1px solid rgba(45, 212, 191, 0.4)',
            borderRadius: 12,
            padding: '14px 20px',
            maxWidth: 380,
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            cursor: 'pointer',
            animation: 'toast-slide-in 0.3s ease-out',
          }}
        >
          {toast.title && (
            <div style={{ fontWeight: 700, fontSize: 14, color: '#ffffff', marginBottom: toast.body ? 4 : 0 }}>
              {toast.title}
            </div>
          )}
          {toast.body && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>
              {toast.body}
            </div>
          )}
        </div>
      )}
      <style>{`
        @keyframes toast-slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
};